'use client';

import { cn } from '@/lib/utils';

import { BlockReferenceArray } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { templateCardStyles } from '@/decodo/blocks/ScrapingTemplatesPreview/Components/ScrapingTemplatePreviewCard';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { LinkButtonStrapiProps } from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import ScrapingTemplatePreviewCard from './Components/ScrapingTemplatePreviewCard';
import VerticalCarousel from './VerticalCarousel';
import ScrapingTemplateCard, {
  ScrapingTemplateCardProps,
} from '../ScrapingTemplateLibrary/Components/ScrapingTemplateCard';

export type ScrapingTemplatesPreviewProps = {
  title?: RichTextFieldStrapiProps;
  description?: RichTextFieldStrapiProps;
  scrapingTemplateCards?: BlockReferenceArray<ScrapingTemplateCardProps>;
  cta?: LinkButtonStrapiProps;
} & Pick<BlockComponentModel<any>, 'theme'>;

const MAX_COLUMNS = 3;

const ScrapingTemplatesPreview = (props: ScrapingTemplatesPreviewProps) => {
  const scrapingTemplateCards = props.scrapingTemplateCards?.data || [];
  const getOrderedCardsForColumnIndex = (columnIndex: number) => {
    const itemsPerColumn = Math.ceil(
      scrapingTemplateCards.length / MAX_COLUMNS
    );

    const cardSets = Array.from({ length: MAX_COLUMNS }).map((_, i) =>
      (scrapingTemplateCards || []).slice(
        i * itemsPerColumn,
        (i + 1) * itemsPerColumn
      )
    );

    const column1 = [...cardSets[0], ...cardSets[1], ...cardSets[2]];
    const column2 = [...cardSets[1], ...cardSets[2], ...cardSets[0]].reverse();
    const column3 = [...cardSets[2], ...cardSets[0], ...cardSets[1]];

    return [column1, column2, column3][columnIndex];
  };

  const renderItem = (
    card: { attributes: ScrapingTemplateCardProps },
    index: number
  ) => (
    <div className='mb-8 w-full'>
      <ScrapingTemplatePreviewCard card={card.attributes} key={index} />
    </div>
  );

  return (
    <BlockWrapper
      data-tracker-section={SECTION_NAMES.SCRAPING_TEMPLATE_PREVIEW}
      contentFit='mid'
    >
      <div className='mx-auto'>
        <div className={cn('mx-auto  md:text-center', 'pb-8 md:pb-20')}>
          {props.title && (
            <div className='mb-5 text-gray-950 md:mb-6'>
              <RichTextFieldStrapi block={props.title} />
            </div>
          )}
          {props.description && (
            <div className='text-body-l font-mono text-gray-500'>
              <RichTextFieldStrapi block={props.description} />
            </div>
          )}
        </div>

        <div>
          <div className='flex-justify-center relative mb-10 mt-5 hidden md:block'>
            <div className='from-base-white dark:from-gray-25 absolute z-40 h-10 w-full bg-gradient-to-b to-transparent' />
            <div
              className={cn(
                'group absolute -ml-4 mr-8 w-full px-4 md:mr-0',
                'overflow-hidden',
                'hidden md:grid ',
                'md:grid-cols-2',
                'desktop:grid-cols-3',
                'gap-8',
                'h-[600px]',
                'z-30'
              )}
            >
              {Array.from({ length: MAX_COLUMNS }).map((_, index) => (
                <VerticalCarousel<{ attributes: ScrapingTemplateCardProps }>
                  items={getOrderedCardsForColumnIndex(index)}
                  renderItem={renderItem}
                  key={index}
                  direction={index % 2 === 0 ? 'up' : 'down'}
                  classNames={{
                    inner: 'group-hover:animation-pause transition-delay-500',
                  }}
                />
              ))}
            </div>
          </div>
          <div
            className={cn(
              'block max-h-[600px] md:hidden',
              'absolute -ml-4 mr-8 overflow-y-auto overflow-x-hidden px-4 md:mr-0'
            )}
          >
            {scrapingTemplateCards.map((card, index) => (
              <div className={cn('mb-5 last:mb-4')} key={index}>
                <ScrapingTemplateCard
                  {...card.attributes}
                  classNames={templateCardStyles}
                />
              </div>
            ))}
          </div>

          <div className='h-[600px]' />
          <div className='relative'>
            <div className='to-base-white dark:to-gray-25 absolute bottom-0 z-40 h-10 w-full bg-gradient-to-b from-transparent' />
          </div>
        </div>
      </div>

      <div className='flex w-full'>
        {props.cta && (
          <LinkButton
            classNames={{
              root: cn('mt-8 md:mt-20', 'w-full md:w-auto mx-auto'),
            }}
            color={props.cta?.color || 'primary'}
            href={props.cta?.url}
          >
            {props.cta?.text}
          </LinkButton>
        )}
      </div>
    </BlockWrapper>
  );
};

export default ScrapingTemplatesPreview;
